import { TradeItem } from '@/types';
import { getEffectiveTradeStatusLabel } from './effective-status';

/**
 * Whitelist of TradeItem fields this client search matches against.
 *
 * Since LR-546 the blotter search is SERVER-authoritative (BlotterSearchFilter in
 * api/v1/positions/views.py) — this util now only backs the MetricsBar pill's
 * fallback basis, so its surface must MIRROR the server `search_fields`: identity
 * text columns + the trade id + the human labels (status/type/agreement/term).
 * Number, date and rate-engine columns are NOT free-text searchable — they are
 * filtered via the sidebar ranges — so they are deliberately absent here (else the
 * pill fallback would diverge from the server, kvi-r2 #1/#14).
 *
 * `status_display` is intentionally NOT here (LR-497): the Status column shows the
 * derived effective label, searched via getEffectiveTradeStatusLabel below so the
 * two derived buckets match by the label the user actually sees.
 */
export const TRADE_SEARCHABLE_FIELDS: readonly (keyof TradeItem)[] = [
  'id',
  'symbol_id',
  'description',
  'isin',
  'counterparty',
  'custodian_name',
  'custodian_account',
  'counterparty_account',
  'collateral_isin',
  'currency',
  'settlement_currency',
  'trade_type_display',
  'agreement_display',
  'term_type_display',
] as const;

/**
 * Substring-match the trade list against `query`, scanning only the fields in
 * `TRADE_SEARCHABLE_FIELDS`. Case-insensitive. An empty/whitespace query
 * returns the input unchanged.
 */
export function applyTradeSearch(data: TradeItem[], query: string): TradeItem[] {
  // Split into terms the same way the server does (whitespace / comma) and AND them —
  // each term must match some whitelisted field or the effective-status label. Keeps this
  // client fallback in step with the multi-term server BlotterSearchFilter (kvi-r4 #2), so
  // `tesla matured` (text + label) matches here too. Empty/whitespace query → unchanged.
  const terms = query.toLowerCase().split(/[\s,]+/).filter(Boolean);
  if (terms.length === 0) return data;
  return data.filter((item) => {
    const haystack = [
      // LR-497: the Status column DISPLAYS the derived effective label, so search matches
      // THAT ("search what you see") — not the raw backend status_display.
      getEffectiveTradeStatusLabel(item).toLowerCase(),
      ...TRADE_SEARCHABLE_FIELDS.map((key) => {
        const value = item[key];
        return value === null || value === undefined ? '' : String(value).toLowerCase();
      }),
    ];
    return terms.every((term) => haystack.some((field) => field.includes(term)));
  });
}
